import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Instagram, Twitter, Youtube, Facebook, Linkedin } from "lucide-react";
import BRAND from "../constants/brand";

export default function Footer() {
  const { t } = useTranslation();
  const cols = [
    { title: t("footer.sections.wellness", "Wellness"), links: [
      { to: "/wellness/mens", label: t("nav.wellness_mens", "Men's Wellness") },
      { to: "/wellness/womens", label: t("nav.wellness_womens", "Women's Wellness") },
      { to: "/wellness/mental", label: t("nav.wellness_mental", "Mental Wellness") },
      { to: "/wellness/nutrition", label: t("nav.wellness_nutrition", "Nutrition") },
      { to: "/wellness/sleep", label: t("nav.wellness_sleep", "Sleep") },
      { to: "/wellness/sports", label: t("nav.wellness_sports", "Sports Performance") },
    ]},
    { title: t("footer.sections.relationships", "Relationships & Families"), links: [
      { to: "/relationships/luv-connect", label: t("nav.rel_luv", "Luv Connect") },
      { to: "/relationships/couples-connect", label: t("nav.rel_couples", "Couples Connect") },
      { to: "/relationships/healthy", label: t("nav.rel_healthy", "Healthy Relationships") },
      { to: "/relationships/communication", label: t("nav.rel_communication", "Communication") },
      { to: "/families/parenting", label: t("nav.fam_parenting", "Parenting") },
      { to: "/families/teens", label: t("nav.fam_teens", "Teens & Youth") },
    ]},
    { title: t("footer.sections.business", "Business & Shop"), links: [
      { to: "/business/partners", label: t("nav.biz_partners", "Partner Businesses") },
      { to: "/business/corporate", label: t("nav.biz_corporate", "Corporate Wellness") },
      { to: "/business/offers", label: t("nav.biz_offers", "Special Offers") },
      { to: "/marketplace", label: t("nav.biz_marketplace", "Marketplace") },
      { to: "/marketplace/register", label: t("nav.list_your_business", "List your business") },
      { to: "/shop", label: t("nav.shop", "Shop") },
    ]},
    { title: t("footer.sections.account", "Tools & Account"), links: [
      { to: "/dosha", label: t("nav.dosha", "Dosha Test") },
      { to: "/ask-donny", label: t("nav.ask_donny", "Ask Donny") },
      { to: "/profile", label: t("nav.profile", "Profile") },
      { to: "/login", label: t("nav.login", "Log in") },
      { to: "/signup", label: t("nav.signup", "Sign up") },
      { to: "/support", label: t("nav.support", "Support") },
    ]},
  ];
  return (
    <footer className="mt-24 border-t border-white/10 bg-[#070708]" data-testid="site-footer">
      <div className="app-container py-14 grid gap-10 md:grid-cols-4 lg:grid-cols-5">
        <div className="lg:col-span-1">
          <Link to="/" className="flex items-center gap-3" aria-label={t("nav.brand_home_aria")}>
            <img
              src={BRAND.LOGO}
              alt="DONiTSH!T"
              width="40"
              height="40"
              className="w-10 h-10 rounded-md object-cover ring-1 ring-[#C8A24A]/30 drop-shadow-[0_0_14px_rgba(212,175,55,0.30)]"
              data-testid="footer-brand-logo"
            />
            <div className="leading-none">
              <div className="font-display text-base">DONiTSH!T</div>
              <div className="text-[10px] tracking-[0.28em] gold-text uppercase">{t("nav.brand_eyebrow")}</div>
            </div>
          </Link>
          <p className="mt-5 text-sm text-white/68 leading-relaxed max-w-[40ch] body-copy">{t("footer.sentence")}</p>
          <div className="flex gap-3 mt-5 text-white/70" data-testid="footer-socials">
            <a href="https://www.instagram.com/donitshit" target="_blank" rel="noreferrer" aria-label="Instagram" className="hover:text-white" data-testid="social-instagram"><Instagram className="w-5 h-5" /></a>
            <a href="https://www.linkedin.com/company/donitshit" target="_blank" rel="noreferrer" aria-label="LinkedIn" className="hover:text-white" data-testid="social-linkedin"><Linkedin className="w-5 h-5" /></a>
            <a href="https://twitter.com/donitshit" target="_blank" rel="noreferrer" aria-label="X / Twitter" className="hover:text-white" data-testid="social-twitter"><Twitter className="w-5 h-5" /></a>
            <a href="https://www.youtube.com/@donitshit" target="_blank" rel="noreferrer" aria-label="YouTube" className="hover:text-white" data-testid="social-youtube"><Youtube className="w-5 h-5" /></a>
            <a href="https://www.facebook.com/donitshit" target="_blank" rel="noreferrer" aria-label="Facebook" className="hover:text-white" data-testid="social-facebook"><Facebook className="w-5 h-5" /></a>
          </div>
        </div>
        {cols.map((c) => (
          <div key={c.title}>
            <div className="text-xs tracking-[0.28em] uppercase gold-text">{c.title}</div>
            <ul className="mt-4 space-y-2">
              {c.links.map((l) => (
                <li key={l.to}>
                  <Link to={l.to} className="text-sm text-white/75 hover:text-white">{l.label}</Link>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
      <div className="border-t border-white/10">
        <div className="app-container py-5 flex flex-col sm:flex-row gap-3 items-center justify-between text-xs text-white/55">
          <div>© {new Date().getFullYear()} DONiTSH!T. {t("footer.rights")}</div>
          <nav
            aria-label={t("footer.legal_aria", "Legal")}
            className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1"
            data-testid="footer-legal-links"
          >
            <Link
              to="/privacy"
              className="hover:text-white transition-colors"
              data-testid="footer-legal-privacy"
            >
              {t("footer.legal_privacy", "Privacy Policy")}
            </Link>
            <span aria-hidden="true" className="text-white/25">·</span>
            <Link
              to="/terms"
              className="hover:text-white transition-colors"
              data-testid="footer-legal-terms"
            >
              {t("footer.legal_terms", "Terms of Use")}
            </Link>
            <span aria-hidden="true" className="text-white/25">·</span>
            <Link
              to="/community-guidelines"
              className="hover:text-white transition-colors"
              data-testid="footer-legal-community"
            >
              {t("footer.legal_community", "Community Guidelines")}
            </Link>
            <span aria-hidden="true" className="text-white/25">·</span>
            <Link
              to="/disclaimer"
              className="hover:text-white transition-colors"
              data-testid="footer-legal-disclaimer"
            >
              {t("footer.legal_disclaimer", "Health & Wellness Disclaimer")}
            </Link>
          </nav>
        </div>
      </div>
    </footer>
  );
}
